import { call } from 'redux-saga/effects';
import { ChildTask, ReaderTaskType } from '@dynamika-shared/types/reader';
import { TASK_STATUSES } from './constants';
import { startReaderSubTask } from '@restore/readerSubTask/readerSubTask.slice';
import { startReaderSubTaskWorker } from '@restore/readerSubTask/readerSubTask.saga';

export function* decideToStartSubTask(
  currentSubTask: Partial<ReaderTaskType>,
  childTasks: ChildTask[],
  endpoint?: string,
  parentTaskId?: string
) {
  const currChildTask = childTasks?.find(t => t.id === currentSubTask.id);

  const prevChildTask = childTasks?.find(
    t => t.visitOrder < (currChildTask?.visitOrder ?? 0)
  );

  const currentSubTaskIsPending =
    currentSubTask?.status === TASK_STATUSES.READING_PENDING ||
    !currentSubTask.status;
  const prevSubTaskIsCompletedOrUndefined =
    prevChildTask?.status === TASK_STATUSES.COMPLETED || !prevChildTask;


  const ableToLoad =
    (currChildTask && prevChildTask?.status === TASK_STATUSES.COMPLETED) ||
    !prevChildTask;

  const shouldStart =
    currentSubTaskIsPending && prevSubTaskIsCompletedOrUndefined;

  // Start the pending sub-task so backend copies lesions from the previous
  // visit, then await the automatic refetch before we hand control back. Using
  // `call` (not `dispatch`) so the saga blocks until start + refetch are both
  // finished — otherwise the form renders against the pre-start, lesion-less
  // snapshot.
  //
  // Must go through the SUB-task worker (not the parent `startReaderTaskWorker`):
  // its post-start refetch writes back into the `readerSubTask` slice, which is
  // the slice `handleGroupTaskSaga` re-reads as `freshSubTask`. The parent
  // worker refetched into `readerTask` instead, so the sub-task snapshot stayed
  // PENDING with zero copied measurements and the first follow-up open rendered
  // no lesion cards until a manual page reload (D7-5719). `endpoint` is threaded
  // through so the refetch hits the same role-scoped task endpoint as the
  // initial fetch.
  // The sub-task record itself does not carry `parentTaskId`, so the caller
  // passes the parent id explicitly. Fall back to the record field if present.
  const resolvedParentTaskId = parentTaskId ?? currentSubTask.parentTaskId ?? undefined;

  if (shouldStart) {
    yield call(
      startReaderSubTaskWorker,
      startReaderSubTask({
        clinicalTrialId: currentSubTask.clinicalTrialId ?? '',
        taskId: resolvedParentTaskId,
        subTaskId: currentSubTask.id ?? '',
        endpoint,
      })
    );
  }

  return {
    ableToLoad,
    shouldStart,
  };
}

export type DecideToStartSubTaskResult =
  ReturnType<typeof decideToStartSubTask> extends Generator<
    unknown,
    infer R,
    unknown
  >
    ? R
    : never;
